/**
 * Care seeker form schemas.
 *
 * Base Zod schemas for care seeker lead forms that apps can extend
 * with their own specific leadSource enums.
 *
 * @module schemas/care-seeker-form
 */

import type { CareOptionKey } from "../types/care-seeker";

import { z } from "zod";
import {
  emailSchema,
  firstNameSchema,
  lastNameSchema,
  phoneSchema,
  postalCodeSchema,
} from "./field-validators";

// ============================================================================
// Form Variant
// ============================================================================

/**
 * Available form variants.
 * Use this constant to reference form variants instead of magic strings.
 * This makes it easier to track and remove old variants.
 */
export const FORM_VARIANTS = {
  V1: "v1",
  V2: "v2",
} as const;

export const formVariantSchema = z
  .enum([FORM_VARIANTS.V1, FORM_VARIANTS.V2])
  .default(FORM_VARIANTS.V1);

// ============================================================================
// Care Recipient
// ============================================================================

export const careRecipientSchema = z.enum(["parent", "spouse", "me", "other"]);

// ============================================================================
// Care Options
// ============================================================================

export const careOptionsSchema = z
  .object({
    everydayTasks: z.boolean().default(false),
    memoryCare: z.boolean().default(false),
    mobilityAssistance: z.boolean().default(false),
    other: z.boolean().default(false),
  })
  .refine((data) => Object.values(data).some((val) => val === true), {
    error: "Please select at least one care option",
  });

// ============================================================================
// Urgency
// ============================================================================

export const urgencySchema = z.enum([
  "right-away",
  "within-a-few-weeks",
  "just-planning-ahead",
  "not-sure",
]);

// ============================================================================
// Base Form Schema
// ============================================================================

/**
 * Base schema for care seeker lead forms.
 *
 * Apps should extend this schema and override step1.leadSource
 * with their own specific enum values.
 *
 * @example
 * ```ts
 * // In hi-care
 * const hiCareLeadSourceSchema = z.enum(["RPN", "PPC"]).default("PPC");
 *
 * export const careSeekerShallowLeadFormSchema = z.object({
 *   ...careSeekerFormSchemaBase.shape,
 *   step1: careSeekerFormSchemaBase.shape.step1.extend({
 *     leadSource: hiCareLeadSourceSchema,
 *   }),
 * });
 * ```
 */
export const careSeekerFormSchemaBase = z.object({
  step1: z.object({
    formVariant: formVariantSchema,
    leadSource: z.string(), // Apps override this with specific enums
    role: z.literal("care-seeker"),
  }),
  step2: z.object({
    careFor: careRecipientSchema,
  }),
  step3: careOptionsSchema,
  step4: z.object({
    location: postalCodeSchema,
    organizationNumber: z.number(),
    postalCode: z.string().optional(), // System-derived value
    urgency: urgencySchema.optional(), // Optional in V1, required in V2
  }),
  step5: z.object({
    consentTerms: z.boolean().optional(),
    email: emailSchema,
    firstName: firstNameSchema,
    lastName: lastNameSchema,
    OptInEmail: z.boolean().default(true),
    OptInSms: z.boolean().default(false),
    phone: phoneSchema,
  }),
});

/**
 * V2 schema with required urgency field.
 *
 * Apps should extend this schema the same way as the base schema,
 * overriding step1.leadSource with their specific enum values.
 */
export const careSeekerFormSchemaBaseV2 = z.object({
  ...careSeekerFormSchemaBase.shape,
  step4: careSeekerFormSchemaBase.shape.step4.extend({
    urgency: urgencySchema, // Required in V2
  }),
});

// ============================================================================
// Type Inference
// ============================================================================

/**
 * Inferred type from the base schema.
 * Apps will have their own specific types with narrower leadSource values.
 */
export type CareSeekerFormData = z.input<typeof careSeekerFormSchemaBase>;

/**
 * Type for care options selection (at least one required).
 */
export type CareOptionSelection = [CareOptionKey, ...CareOptionKey[]];
